// src/pages/Documents/DocumentPreviewModal.jsx
import React, { useState, useEffect } from 'react';
import { Modal, Form } from 'react-bootstrap';
import { Card, Button } from 'components';
import { AiFillCloseCircle } from 'react-icons/ai';
import { FiSave, FiX } from 'react-icons/fi';

const DocumentPreviewModal = ({ _this }) => {
  const {
    showDocumentPreviewModalOpen,
    setShowDocumentPreviewModalOpen,
    selectedDoc,
    renameDocument
  } = _this;

  const [isEditing, setIsEditing] = useState(false);
  const [renameType, setRenameType] = useState('');
  const [newTitle, setNewTitle] = useState('');

  useEffect(() => {
    if (selectedDoc) setNewTitle(selectedDoc.message);
  }, [selectedDoc]);

  if (!selectedDoc) return null;

  const handleRenameSave = () => {
    if (!newTitle.trim()) return;

    renameDocument(selectedDoc._id, newTitle.trim());
    setIsEditing(false);
    setRenameType('');
  };

  const handleCancel = () => {
    setIsEditing(false);
    setRenameType('');
    setNewTitle(selectedDoc.message);
  };

  const isSaveDisabled =
    !renameType ||
    (renameType === 'Other' && !newTitle.trim()) ||
    newTitle.trim() === selectedDoc.message.trim();

  return (
    <Modal
      show={showDocumentPreviewModalOpen}
      onHide={() => setShowDocumentPreviewModalOpen(false)}
      size="xl"
      centered
      style={{ zIndex: 9999 }}
    >
      <Modal.Header>
        <Modal.Title className="text-xl font-semibold">Document Preview</Modal.Title>
        <AiFillCloseCircle
          size={26}
          className="cursor-pointer text-gray-600 hover:text-red-500"
          onClick={() => setShowDocumentPreviewModalOpen(false)}
        />
      </Modal.Header>

      <Modal.Body className="bg-gray-50">
        <Card className="p-4 bg-white shadow-md rounded-lg border border-gray-200">
          {/* --- Title Section --- */}
          <div className="flex flex-col gap-3">
            {/* Save/Cancel Row (only when editing) */}
            {isEditing && (
              <div className="flex items-center justify-end gap-2">
                <div>
                  <Button
                    variant="success"
                    size="sm"
                    disabled={isSaveDisabled}
                    onClick={() => handleRenameSave()}
                    className="flex items-center gap-1"
                  >
                    Save
                  </Button>
                </div>
                <div>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleCancel}
                    className="flex items-center gap-1"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* Title + Rename Row */}
            <div className="flex justify-between items-center">
              <h3 className="text-base font-medium text-gray-800 flex items-center gap-2">
                Title:{' '}
                {renameType === 'Other' ? (
                  <Form.Control
                    type="text"
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.target.value)}
                    className="w-80 inline-block"
                    autoFocus
                  />
                ) : (
                  <span className="font-semibold">{newTitle || selectedDoc.message}</span>
                )}
              </h3>

              {/* Right side: Rename button OR dropdown */}
              {!isEditing ? (
                <div>
                  <Button
                    size="sm"
                    variant="light"
                    onClick={() => setIsEditing(true)}
                    className="flex justify-center items-center gap-1"
                  >
                    Rename
                  </Button>
                </div>
              ) : (
                <Form.Select
                  size="sm"
                  value={renameType}
                  onChange={(e) => {
                    const value = e.target.value;
                    setRenameType(value);
                    if (value === 'HSPP Authorization Form') {
                      setNewTitle('HSPP Authorization Form');
                    } else if (value === 'Other') {
                      setNewTitle(selectedDoc.message);
                    }
                  }}
                  className="w-64"
                >
                  <option value="">Select Rename Type</option>
                  <option value="HSPP Authorization Form">HSPP Authorization Form</option>
                  <option value="Other">Other</option>
                </Form.Select>
              )}
            </div>
          </div>

          {/* --- File Preview --- */}
          <div className="w-full flex justify-center items-center mt-6">
            <iframe
              src={selectedDoc.previewUrl}
              title="Document Preview"
              className="rounded-lg border border-gray-300 shadow-sm"
              style={{
                width: '90%',
                height: '65vh',
                maxHeight: '650px'
              }}
            />
          </div>
        </Card>
      </Modal.Body>

      <Modal.Footer>
        <Button onClick={() => setShowDocumentPreviewModalOpen(false)}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DocumentPreviewModal;
